import React, { useState, useEffect, useCallback, useRef } from "react";
import DeltaBadge from "../components/ui/DeltaBadge";

const API = "https://ceo-cockpit-3npo.onrender.com";

const YEARS    = ["FY22","FY23","FY24","FY25","FY26","FY27"];
const QUARTERS = ["Q1","Q2","Q3","Q4"];
const HALVES   = ["H1","H2"];
const MONTHS   = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];

// Retail sub-channels
const RETAIL_SUBS = ["Retail - DLF Emporio","Retail - Kila","Retail - Khan","Retail - Mumbai","Retail - Dhan Mill"];
// Top-level channels (for Total Calc — no double counting)

// All channels in order
const ALL_CHANNELS = [
  "All Channels (Total Co.)",
  "Retail",
  ...RETAIL_SUBS,
  "E-Commerce",
  "MBO's & Others",
  "Trunk Show",
];

function toDbMonth(fy: string, m: string): string {
  const n   = parseInt(fy.slice(2));
  const h1  = String(n - 1);
  const h2b = String(n);
  const h1m  = ["Apr","May","Jun","Jul","Aug","Sep"];
  const h2am = ["Oct","Nov","Dec"];
  if (h1m.includes(m) || h2am.includes(m)) return `${m}-${h1}`;
  return `${m}-${h2b}`;
}

function qMonths(q: string): string[] {
  const map: Record<string,string[]> = {
    Q1:["Apr","May","Jun"], Q2:["Jul","Aug","Sep"],
    Q3:["Oct","Nov","Dec"], Q4:["Jan","Feb","Mar"],
  };
  return map[q] || [];
}

function toDbParam(period: string, fy: string): string {
  if (period === "Annual") return "Annual";
  if (period === "YTD")    return "YTD";
  if (period === "H1") return ["Apr","May","Jun","Jul","Aug","Sep"].map(m=>toDbMonth(fy,m)).join(",");
  if (period === "H2") return ["Oct","Nov","Dec","Jan","Feb","Mar"].map(m=>toDbMonth(fy,m)).join(",");
  if (["Q1","Q2","Q3","Q4"].includes(period)) return qMonths(period).map(m=>toDbMonth(fy,m)).join(",");
  return toDbMonth(fy, period);
}

function periodOptions(mode: string): string[] {
  if (mode === "QoQ")      return QUARTERS;
  if (mode === "BiYearly") return HALVES;
  if (mode === "YoY")      return [];
  return [...MONTHS, "YTD"];
}

interface SelItem { year: string; period: string; }
function selKey(s: SelItem) { return `${s.year}||${s.period}`; }

interface ChannelData {
  selected_value: number | null;
  compare_value:  number | null;
  delta:          number | string | null;
}

interface ChannelRow {
  metric:         string;
  type:           "absolute" | "percent";
  channels:       Record<string, ChannelData>;
  total_calc:     ChannelData;
  total_mismatch: boolean;
}

function fmtVal(val: number | null, type: string): string {
  if (val === null || val === undefined) return "--";
  if (type === "percent") return `${val.toFixed(2)}%`;
  if (val < 0) return `(${Math.abs(val).toFixed(2)})`;
  return val.toFixed(2);
}

function valColor(val: number | null, type: string): string {
  if (val === null) return "var(--text-muted)";
  if (type === "absolute" && val < 0) return "var(--delta-negative)";
  if (type === "percent"  && val < 0) return "var(--delta-negative)";
  return "var(--text-number)";
}

// ── Mode Dropdown ─────────────────────────────────────────────────────────────
function ModeDropdown({ value, onChange }: { value: string; onChange: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const modes = ["MoM","QoQ","BiYearly","YoY"];
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{position:"relative",display:"inline-block"}}>
      <button onClick={()=>setOpen(p=>!p)} style={{
        padding:"6px 12px",borderRadius:"6px",border:"1px solid var(--border)",
        backgroundColor:"var(--bg-secondary)",color:"var(--text-primary)",
        fontSize:"12px",fontWeight:600,cursor:"pointer",
        display:"flex",alignItems:"center",gap:"8px",minWidth:"130px",justifyContent:"space-between",
      }}>
        <span>Mode: {value}</span>
        <span style={{fontSize:"10px"}}>{open?"▲":"▼"}</span>
      </button>
      {open && (
        <div style={{
          position:"absolute",top:"calc(100% + 4px)",left:0,
          backgroundColor:"var(--bg-primary)",border:"1px solid var(--border)",
          borderRadius:"8px",zIndex:999,minWidth:"140px",
          boxShadow:"0 8px 24px rgba(0,0,0,0.15)",overflow:"hidden",
        }}>
          {modes.map(m=>(
            <div key={m} onClick={()=>{onChange(m);setOpen(false);}} style={{
              padding:"8px 16px",cursor:"pointer",fontSize:"12px",
              fontWeight:m===value?600:400,
              color:m===value?"var(--accent-gold)":"var(--text-primary)",
              backgroundColor:m===value?"rgba(201,168,76,0.1)":"transparent",
              borderBottom:"1px solid var(--border)",
              display:"flex",alignItems:"center",gap:"8px",
            }}
            onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor="var(--bg-secondary)"}
            onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor=m===value?"rgba(201,168,76,0.1)":"transparent"}
            >
              <span>{m===value?"●":"○"}</span>{m}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Multi-Year Period Dropdown ────────────────────────────────────────────────
interface MultiYearDropdownProps {
  label:      string;
  mode:       string;
  selections: SelItem[];
  onToggle:   (item: SelItem) => void;
}

function MultiYearDropdown({ label, mode, selections, onToggle }: MultiYearDropdownProps) {
  const [open,     setOpen]     = useState(false);
  const [expanded, setExpanded] = useState<Record<string,boolean>>({});
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const opts    = periodOptions(mode);
  const selKeys = new Set(selections.map(selKey));

  const displayLabel = selections.length === 0
    ? "Select..."
    : selections.length === 1
      ? mode==="YoY" ? selections[0].year : `${selections[0].period} ${selections[0].year}`
      : `${selections.length} selected`;

  return (
    <div ref={ref} style={{position:"relative",display:"inline-block"}}>
      <button onClick={()=>setOpen(p=>!p)} style={{
        padding:"6px 12px",borderRadius:"6px",
        border:"1px solid var(--accent-gold)",
        backgroundColor:"transparent",color:"var(--accent-gold)",
        fontSize:"12px",fontWeight:600,cursor:"pointer",
        display:"flex",alignItems:"center",gap:"6px",
        minWidth:"160px",justifyContent:"space-between",
      }}>
        <span>{label}: {displayLabel}</span>
        <span style={{fontSize:"10px"}}>{open?"▲":"▼"}</span>
      </button>
      {open && (
        <div style={{
          position:"absolute",top:"calc(100% + 4px)",left:0,
          backgroundColor:"var(--bg-primary)",border:"1px solid var(--border)",
          borderRadius:"8px",zIndex:999,minWidth:"220px",maxHeight:"360px",
          overflowY:"auto",boxShadow:"0 8px 24px rgba(0,0,0,0.15)",
        }}>
          {YEARS.map(fy=>{
            if (mode === "YoY") {
              const item = {year:fy, period:"Annual"};
              const isOn = selKeys.has(selKey(item));
              return (
                <div key={fy} onClick={()=>onToggle(item)} style={{
                  padding:"8px 16px",cursor:"pointer",
                  display:"flex",alignItems:"center",gap:"8px",
                  fontSize:"12px",fontWeight:isOn?600:400,
                  color:isOn?"var(--accent-gold)":"var(--text-primary)",
                  backgroundColor:isOn?"rgba(201,168,76,0.1)":"transparent",
                  borderBottom:"1px solid var(--border)",
                }}
                onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor="var(--bg-secondary)"}
                onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor=isOn?"rgba(201,168,76,0.1)":"transparent"}
                >
                  <input type="checkbox" checked={isOn} readOnly style={{accentColor:"var(--accent-gold)",width:"13px",height:"13px",pointerEvents:"none"}}/>
                  {fy}
                </div>
              );
            }
            const anySelected = opts.some(p=>selKeys.has(selKey({year:fy,period:p})));
            const isExp = expanded[fy] ?? false;
            return (
              <div key={fy}>
                <div onClick={()=>setExpanded(prev=>({...prev,[fy]:!prev[fy]}))} style={{
                  padding:"8px 12px",cursor:"pointer",
                  display:"flex",alignItems:"center",justifyContent:"space-between",
                  backgroundColor:anySelected?"rgba(201,168,76,0.06)":"transparent",
                  borderBottom:"1px solid var(--border)",
                  fontSize:"12px",fontWeight:anySelected?600:400,
                  color:anySelected?"var(--accent-gold)":"var(--text-primary)",
                }}
                onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor="var(--bg-secondary)"}
                onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor=anySelected?"rgba(201,168,76,0.06)":"transparent"}
                >
                  <span>{fy} {anySelected && `(${opts.filter(p=>selKeys.has(selKey({year:fy,period:p}))).length})`}</span>
                  <span style={{fontSize:"10px"}}>{isExp?"▼":"▶"}</span>
                </div>
                {isExp && (
                  <div style={{backgroundColor:"var(--bg-secondary)"}}>
                    {opts.map(p=>{
                      const item = {year:fy,period:p};
                      const isOn = selKeys.has(selKey(item));
                      return (
                        <div key={p} onClick={()=>onToggle(item)} style={{
                          padding:"6px 12px 6px 28px",cursor:"pointer",
                          display:"flex",alignItems:"center",gap:"8px",
                          fontSize:"12px",
                          color:isOn?"var(--accent-gold)":"var(--text-secondary)",
                          backgroundColor:isOn?"rgba(201,168,76,0.08)":"transparent",
                          borderBottom:"1px solid var(--border)",
                        }}
                        onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor="rgba(201,168,76,0.05)"}
                        onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor=isOn?"rgba(201,168,76,0.08)":"transparent"}
                        >
                          <input type="checkbox" checked={isOn} readOnly style={{accentColor:"var(--accent-gold)",width:"13px",height:"13px",pointerEvents:"none"}}/>
                          {p}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ on, onToggle, label }: { on:boolean; onToggle:()=>void; label:string }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer"}} onClick={onToggle}>
      <div style={{
        width:"34px",height:"18px",borderRadius:"9px",
        backgroundColor:on?"var(--accent-gold)":"var(--border)",
        position:"relative",transition:"background-color 0.2s",flexShrink:0,
      }}>
        <div style={{
          position:"absolute",top:"2px",left:on?"18px":"2px",
          width:"14px",height:"14px",borderRadius:"50%",backgroundColor:"#fff",
          transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)",
        }}/>
      </div>
      <span style={{fontSize:"11px",color:"var(--text-secondary)",fontWeight:500}}>{label}</span>
    </div>
  );
}

// ── Main MISChannelwise ───────────────────────────────────────────────────────
export default function MISChannelwise() {
  const [mode,        setMode]        = useState("MoM");
  const [baseSels,    setBaseSels]    = useState<SelItem[]>([{year:"FY27",period:"YTD"}]);
  const [deltaOn,     setDeltaOn]     = useState(true);
  const [cmpSels,     setCmpSels]     = useState<SelItem[]>([{year:"FY26",period:"YTD"}]);
  const [mirrorCmp,   setMirrorCmp]   = useState(true);
  const [retailExpanded, setRetailExpanded] = useState(false);
  const [showMRP, setShowMRP] = useState(true);
  const [chDropOpen, setChDropOpen] = useState(false);
  const chDropRef = React.useRef<HTMLDivElement>(null);

  // Close channel dropdown on outside click
  React.useEffect(() => {
    const h = (e: MouseEvent) => {
      if (chDropRef.current && !chDropRef.current.contains(e.target as Node)) setChDropOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const [visibleCh,   setVisibleCh]   = useState<Set<string>>(
    new Set(["All Channels (Total Co.)","Retail","E-Commerce","MBO's & Others","Trunk Show"])
  );

  // colData: key = selKey(item), value = ChannelRow[]
  const [colData, setColData] = useState<Record<string, ChannelRow[]>>({});
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string|null>(null);

  // Reset on mode change
  useEffect(() => {
    const def = mode==="QoQ"?"Q1":mode==="BiYearly"?"H1":mode==="YoY"?"Annual":"YTD";
    setBaseSels([{year:"FY27",period:def}]);
    setCmpSels([{year:"FY26",period:def}]);
    setMirrorCmp(true);
  }, [mode]);

  // Mirror compare
  useEffect(() => {
    if (!mirrorCmp) return;
    const mirrored = baseSels.map(s => {
      const n = parseInt(s.year.slice(2));
      return {year:`FY${n-1}`, period:s.period};
    });
    setCmpSels(mirrored);
  }, [baseSels, mirrorCmp]);

  function toggleBase(item: SelItem) {
    const k = selKey(item);
    setBaseSels(prev => {
      const exists = prev.some(s=>selKey(s)===k);
      if (exists) return prev.length>1 ? prev.filter(s=>selKey(s)!==k) : prev;
      return [...prev, item];
    });
  }
  function toggleCmp(item: SelItem) {
    setMirrorCmp(false);
    const k = selKey(item);
    setCmpSels(prev => {
      const exists = prev.some(s=>selKey(s)===k);
      if (exists) return prev.length>1 ? prev.filter(s=>selKey(s)!==k) : prev;
      return [...prev, item];
    });
  }

  // Channel visibility toggle
  function toggleChannel(ch: string) {
    setVisibleCh(prev => {
      const next = new Set(prev);
      if (next.has(ch)) {
        next.delete(ch);
        // Hiding Retail also hides sub-channels
        if (ch === "Retail") RETAIL_SUBS.forEach(s=>next.delete(s));
      } else {
        next.add(ch);
        // Selecting Retail deselects all sub-channels (mutually exclusive like YTD vs months)
        if (ch === "Retail") RETAIL_SUBS.forEach(s=>next.delete(s));
        // Selecting a sub-channel deselects Retail
        if (RETAIL_SUBS.includes(ch)) next.delete("Retail");
      }
      return next;
    });
  }

  // Build visible channel list for rendering
  function visibleChannels(): string[] {
    const result: string[] = [];
    for (const ch of ALL_CHANNELS) {
      if (!visibleCh.has(ch)) continue;
      if (RETAIL_SUBS.includes(ch) && !retailExpanded) continue;
      result.push(ch);
    }
    return result;
  }

  const fetchAll = useCallback(async () => {
    if (baseSels.length === 0) return;
    setLoading(true); setError(null);
    try {
      const results: Record<string, ChannelRow[]> = {};
      for (const item of baseSels) {
        const bDb = toDbParam(item.period, item.year);
        const cmpItem = mirrorCmp
          ? cmpSels.find(c=>c.period===item.period) || cmpSels[0]
          : cmpSels[baseSels.indexOf(item)] || cmpSels[0];
        const cDb     = cmpItem ? toDbParam(cmpItem.period, cmpItem.year) : bDb;
        const cmpYear = cmpItem ? cmpItem.year : item.year;
        const url = `${API}/channel-pnl?fiscal_year=${item.year}&months=${encodeURIComponent(bDb)}&compare_year=${cmpYear}&compare_months=${encodeURIComponent(cDb)}&delta=${deltaOn}`;
        const res  = await fetch(url);
        if (!res.ok) throw new Error("API error");
        const json = await res.json();
        results[selKey(item)] = json.rows || [];
      }
      setColData(results);
    } catch {
      setError("Failed to load data. Is the API running?");
    } finally {
      setLoading(false);
    }
  }, [baseSels, cmpSels, mirrorCmp, deltaOn]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const firstKey    = baseSels.length > 0 ? selKey(baseSels[0]) : "";
  const templateRows = colData[firstKey] || [];
  const visCh       = visibleChannels();
  const showTotal   = true; // Always show Total (Calc) column

  const COL_W   = 100;
  const LABEL_W = 200;


  return (
    <div>
      {/* Page Header */}
      <div style={{marginBottom:"16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <h1 style={{fontSize:"18px",fontWeight:700,color:"var(--text-primary)",margin:0}}>MIS Channelwise</h1>
          <p style={{fontSize:"12px",color:"var(--text-secondary)",margin:"2px 0 0"}}>Channel-wise P&L - Rs. Lakhs</p>
        </div>
      </div>

      {/* Filter Panel */}
      <div style={{backgroundColor:"var(--bg-secondary)",border:"1px solid var(--border)",borderRadius:"8px",padding:"14px 16px",marginBottom:"12px"}}>

        {/* Row 1: Mode + View dropdown */}
        <div style={{display:"flex",gap:"12px",alignItems:"center",marginBottom:"12px",flexWrap:"wrap"}}>
          <ModeDropdown value={mode} onChange={setMode}/>
          <MultiYearDropdown label="View" mode={mode} selections={baseSels} onToggle={toggleBase}/>
        </div>

        {/* Divider */}
        <div style={{borderTop:"1px solid var(--border)",margin:"8px 0"}}/>

        {/* Row 2: Delta */}
        <div style={{display:"flex",gap:"12px",alignItems:"center",flexWrap:"wrap"}}>
          <Toggle on={deltaOn} onToggle={()=>setDeltaOn(p=>!p)} label="Show Delta"/>
          {deltaOn && (
            <>
              <span style={{fontSize:"11px",color:"var(--text-muted)",fontWeight:600}}>VS</span>
              <MultiYearDropdown label="Compare" mode={mode} selections={cmpSels} onToggle={toggleCmp}/>
              <label style={{display:"flex",alignItems:"center",gap:"6px",cursor:"pointer",marginLeft:"auto"}}>
                <input type="checkbox" checked={mirrorCmp}
                  onChange={e=>setMirrorCmp(e.target.checked)}
                  style={{accentColor:"var(--accent-gold)"}}/>
                <span style={{fontSize:"11px",color:"var(--text-secondary)"}}>Mirror base period</span>
              </label>
            </>
          )}
        </div>
      </div>

      {/* Channel Filter — Dropdown */}
      <div style={{position:"relative",display:"inline-block",marginBottom:"16px"}} ref={chDropRef}>
        <button onClick={()=>setChDropOpen(p=>!p)} style={{
          padding:"6px 14px",borderRadius:"6px",border:"1px solid var(--border)",
          backgroundColor:"var(--bg-secondary)",color:"var(--text-primary)",
          fontSize:"12px",fontWeight:600,cursor:"pointer",
          display:"flex",alignItems:"center",gap:"8px",
        }}>
          <span>Channels: {visibleCh.size === 5 ? "All" : `${visibleCh.size} selected`}</span>
          <span style={{fontSize:"10px"}}>{chDropOpen?"▲":"▼"}</span>
        </button>
        {chDropOpen && (
          <div style={{
            position:"absolute",top:"calc(100% + 4px)",left:0,
            backgroundColor:"var(--bg-primary)",border:"1px solid var(--border)",
            borderRadius:"8px",zIndex:999,minWidth:"200px",
            boxShadow:"0 8px 24px rgba(0,0,0,0.15)",padding:"8px 0",
          }}>
            {/* Select All */}
            <label style={{display:"flex",alignItems:"center",gap:"8px",padding:"6px 14px",cursor:"pointer",
              fontSize:"12px",fontWeight:600,color:"var(--accent-gold)",borderBottom:"1px solid var(--border)"}}>
              <input type="checkbox"
                checked={visibleCh.size >= 5}
                onChange={()=>{
                  if(visibleCh.size>=5){
                    setVisibleCh(new Set());
                  } else {
                    setVisibleCh(new Set(["All Channels (Total Co.)","Retail","E-Commerce","MBO's & Others","Trunk Show"]));
                  }
                }}
                style={{accentColor:"var(--accent-gold)",width:"13px",height:"13px"}}/>
              Select All
            </label>
            {/* All Channels */}
            <label style={{display:"flex",alignItems:"center",gap:"8px",padding:"6px 14px",cursor:"pointer",
              fontSize:"12px",color:visibleCh.has("All Channels (Total Co.)")?"var(--accent-gold)":"var(--text-secondary)"}}>
              <input type="checkbox" checked={visibleCh.has("All Channels (Total Co.)")} onChange={()=>toggleChannel("All Channels (Total Co.)")}
                style={{accentColor:"var(--accent-gold)",width:"13px",height:"13px"}}/>
              All Channels
            </label>
            <div style={{height:"1px",backgroundColor:"var(--border)",margin:"4px 0"}}/>
            {/* Retail + Sub */}
            <label style={{display:"flex",alignItems:"center",gap:"8px",padding:"6px 14px",cursor:"pointer",
              fontSize:"12px",color:visibleCh.has("Retail")?"var(--accent-gold)":"var(--text-secondary)"}}>
              <input type="checkbox" checked={visibleCh.has("Retail")} onChange={()=>toggleChannel("Retail")}
                style={{accentColor:"var(--accent-gold)",width:"13px",height:"13px"}}/>
              Retail
            </label>
            <div style={{display:"flex",alignItems:"center",padding:"2px 14px 4px"}}>
              <button onClick={()=>setRetailExpanded(p=>!p)} style={{
                padding:"2px 8px",borderRadius:"4px",border:"1px solid var(--border)",
                backgroundColor:"transparent",cursor:"pointer",fontSize:"10px",color:"var(--text-muted)",
              }}>{retailExpanded?"▼ Sub":"▶ Sub"}</button>
            </div>
            {retailExpanded && RETAIL_SUBS.map(ch=>(
              <label key={ch} style={{display:"flex",alignItems:"center",gap:"8px",padding:"4px 14px 4px 28px",cursor:"pointer",
                fontSize:"11px",color:visibleCh.has(ch)?"var(--accent-gold)":"var(--text-muted)"}}>
                <input type="checkbox" checked={visibleCh.has(ch)} onChange={()=>toggleChannel(ch)}
                  style={{accentColor:"var(--accent-gold)",width:"12px",height:"12px"}}/>
                {ch.replace("Retail - ","")}
              </label>
            ))}
            <div style={{height:"1px",backgroundColor:"var(--border)",margin:"4px 0"}}/>
            {/* Other channels */}
            {["E-Commerce","MBO's & Others","Trunk Show"].map(ch=>(
              <label key={ch} style={{display:"flex",alignItems:"center",gap:"8px",padding:"6px 14px",cursor:"pointer",
                fontSize:"12px",color:visibleCh.has(ch)?"var(--accent-gold)":"var(--text-secondary)"}}>
                <input type="checkbox" checked={visibleCh.has(ch)} onChange={()=>toggleChannel(ch)}
                  style={{accentColor:"var(--accent-gold)",width:"13px",height:"13px"}}/>
                {ch}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* MRP Row Toggle */}
      <div style={{marginBottom:"12px",display:"flex",alignItems:"center",gap:"8px"}}>
        <span style={{fontSize:"11px",fontWeight:600,color:"var(--text-muted)"}}>MRP ROW</span>
        <button onClick={()=>setShowMRP(p=>!p)} style={{
          padding:"4px 12px",borderRadius:"5px",
          border:"1px solid var(--border)",
          backgroundColor:showMRP?"rgba(201,168,76,0.1)":"transparent",
          color:showMRP?"var(--accent-gold)":"var(--text-muted)",
          fontSize:"11px",fontWeight:600,cursor:"pointer",
        }}>
          {showMRP ? "Hide MRP" : "Show MRP"}
        </button>
      </div>

      {/* States */}
      {loading && <div style={{textAlign:"center",padding:"60px",color:"var(--text-muted)"}}>Loading data...</div>}
      {error   && <div style={{padding:"16px",backgroundColor:"var(--bg-secondary)",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--text-secondary)",fontSize:"13px"}}>{error}</div>}

      {/* Table */}
      {!loading && !error && templateRows.length > 0 && (
        <div style={{border:"1px solid var(--border)",borderRadius:"8px",overflow:"hidden"}}>
          <div style={{overflowX:"auto"}}>
            <div style={{minWidth:`${LABEL_W + baseSels.length * visCh.length * COL_W + (showTotal?COL_W:0)}px`}}>

              {/* Header — two levels */}
              {/* Level 1: Period labels */}
              <div style={{display:"flex",backgroundColor:"var(--bg-header)",position:"sticky",top:0,zIndex:4}}>
                <div style={{
                  width:LABEL_W,minWidth:LABEL_W,padding:"8px 12px",
                  fontSize:"11px",fontWeight:600,color:"var(--text-header)",
                  position:"sticky",left:0,backgroundColor:"var(--bg-header)",zIndex:5,
                  borderRight:"1px solid var(--border-sidebar)",
                }}/>
                {baseSels.map(item=>{
                  const cmpItem = mirrorCmp
                    ? cmpSels.find(c=>c.period===item.period)||cmpSels[0]
                    : cmpSels[baseSels.indexOf(item)]||cmpSels[0];
                  const colLabel = item.period==="Annual" ? item.year : `${item.period} ${item.year}`;
                  const cmpLabel = cmpItem
                    ? (cmpItem.period==="Annual" ? cmpItem.year : `${cmpItem.period} ${cmpItem.year}`)
                    : "";
                  return (
                    <div key={selKey(item)} style={{
                      width:visCh.length*COL_W, minWidth:visCh.length*COL_W,
                      padding:"6px 8px", textAlign:"center",
                      fontSize:"11px", fontWeight:700,
                      color:"var(--accent-gold)",
                      borderLeft:"2px solid var(--accent-gold)",
                      borderBottom:"1px solid var(--border-sidebar)",
                    }}>
                      {colLabel}{deltaOn && cmpLabel ? ` vs ${cmpLabel}` : ""}
                    </div>
                  );
                })}
                <div style={{
                  width:COL_W, minWidth:COL_W, padding:"6px 8px",
                  textAlign:"center", fontSize:"11px", fontWeight:700,
                  color:"var(--accent-gold)", borderLeft:"2px solid var(--accent-gold)",
                  borderBottom:"1px solid var(--border-sidebar)",
                }}>
                  Total (Calc)
                </div>
              </div>

              {/* Level 2: Channel names */}
              <div style={{display:"flex",backgroundColor:"var(--bg-header)",position:"sticky",top:"30px",zIndex:3}}>
                <div style={{
                  width:LABEL_W,minWidth:LABEL_W,padding:"8px 12px",
                  fontSize:"11px",fontWeight:600,color:"var(--text-header)",
                  position:"sticky",left:0,backgroundColor:"var(--bg-header)",zIndex:4,
                  borderRight:"1px solid var(--border-sidebar)",
                }}>
                  Metric
                </div>
                {baseSels.map(item=>(
                  <React.Fragment key={selKey(item)}>
                    {visCh.map(ch=>(
                      <div key={ch} style={{
                        width:COL_W,minWidth:COL_W,padding:"8px 4px",
                        fontSize:"9px",fontWeight:600,
                        color:ch==="All Channels (Total Co.)"?"var(--accent-gold)":"var(--text-header)",
                        textAlign:"center",
                        borderLeft:"1px solid var(--border-sidebar)",
                        lineHeight:1.3,
                      }}>
                        {ch==="All Channels (Total Co.)"?"All Ch":ch.replace("Retail - ","")}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
                <div style={{
                  width:COL_W,minWidth:COL_W,padding:"8px 4px",
                  fontSize:"9px",fontWeight:600,color:"var(--accent-gold)",
                  textAlign:"center",borderLeft:"2px solid var(--accent-gold)",
                }}>
                  Calc Sum
                </div>
              </div>

              {/* Data rows */}
              {templateRows.filter(row => row.metric !== 'MRP' || showMRP).map((row,idx)=>{
                const isAlt = idx%2===1;
                return (
                  <div key={row.metric} style={{
                    display:"flex",
                    backgroundColor:isAlt?"var(--bg-row-alt)":"var(--bg-primary)",
                    borderTop:"1px solid var(--border)",
                  }}>
                    {/* Frozen metric label */}
                    <div style={{
                      width:LABEL_W,minWidth:LABEL_W,padding:"6px 12px",
                      fontSize:"11px",fontWeight:600,color:"var(--text-primary)",
                      position:"sticky",left:0,
                      backgroundColor:isAlt?"var(--bg-row-alt)":"var(--bg-primary)",
                      zIndex:1,borderRight:"1px solid var(--border)",
                    }}>
                      {row.metric}
                    </div>

                    {/* Period × Channel cells */}
                    {baseSels.map(item=>(
                      <React.Fragment key={selKey(item)}>
                        {visCh.map(ch=>{
                          const rows = colData[selKey(item)] || [];
                          const r    = rows.find(x=>x.metric===row.metric);
                          const cd   = r?.channels[ch];
                          return (
                            <div key={ch} style={{
                              width:COL_W,minWidth:COL_W,padding:"5px 4px",
                              borderLeft:"1px solid var(--border)",textAlign:"center",
                            }}>
                              <div className="num" style={{
                                fontSize:"11px",
                                fontWeight:ch==="All Channels (Total Co.)"?600:400,
                                color:valColor(cd?.selected_value??null,row.type),
                              }}>
                                {fmtVal(cd?.selected_value??null,row.type)}
                              </div>
                              {deltaOn && <div className="num" style={{fontSize:"10px",color:"var(--text-muted)",marginTop:"1px"}}>
                                {fmtVal(cd?.compare_value??null,row.type)}
                              </div>}
                              {deltaOn && <div style={{marginTop:"2px"}}>
                                <DeltaBadge value={cd?.delta??null} rowType={row.type} isCost={false}/>
                              </div>}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}

                    {/* Total (Calc) — sum of visible top-level channels only */}
                    {showTotal && row.metric === 'COGS %' && (
                      <div style={{
                        width:COL_W,minWidth:COL_W,padding:"5px 4px",
                        borderLeft:"2px solid var(--accent-gold)",textAlign:"center",
                        backgroundColor:"rgba(201,168,76,0.04)",
                        fontSize:"10px",fontStyle:"italic",color:"var(--text-muted)",
                      }}>
                        For Future
                      </div>
                    )}
                    {showTotal && row.metric !== 'COGS %' && (()=>{
                      // Dynamic channel list — Retail OR its sub-channels (never both)
                      const TOP_CH: string[] = [];
                      if (visibleCh.has("Retail")) {
                        TOP_CH.push("Retail");
                      } else {
                        RETAIL_SUBS.forEach(s => { if (visibleCh.has(s)) TOP_CH.push(s); });
                      }
                      ["E-Commerce","MBO's & Others","Trunk Show"].forEach(ch => {
                        if (visibleCh.has(ch)) TOP_CH.push(ch);
                      });
                      // PCT rows: recalculate from summed absolutes
                      const PCT_MAP: Record<string,[string,string]> = {
                        "COGS %": ["COGS","Net of Taxes"],
                        "Gross Margin %": ["Gross Margin","Net of Taxes"],
                        "Opex % of Net Sales": ["Total Operating Expenses","Net of Taxes"],
                        "Contribution Margin %": ["Contribution Margin","Net of Taxes"],
                      };

                      let totalVal: number|null = null;
                      // Exclude YTD if individual months also selected — avoid double counting
                      const hasIndividualMonths = baseSels.some(s => s.period !== "YTD" && s.period !== "Annual");
                      const periodsForTotal = hasIndividualMonths
                        ? baseSels.filter(s => s.period !== "YTD" && s.period !== "Annual")
                        : baseSels;

                      if (row.type === "percent" && PCT_MAP[row.metric]) {
                        const [numMetric, denMetric] = PCT_MAP[row.metric];
                        let numSum = 0; let denSum = 0;
                        periodsForTotal.forEach(item => {
                          const rows = colData[selKey(item)] || [];
                          const numRow = rows.find(x=>x.metric===numMetric);
                          const denRow = rows.find(x=>x.metric===denMetric);
                          TOP_CH.forEach(ch => {
                            if (!visibleCh.has(ch)) return;
                            numSum += numRow?.channels[ch]?.selected_value ?? 0;
                            denSum += denRow?.channels[ch]?.selected_value ?? 0;
                          });
                        });
                        totalVal = denSum !== 0 ? (numSum / denSum) * 100 : null;
                      } else {
                        let sum = 0; let hasVal = false;
                        periodsForTotal.forEach(item => {
                          const rows = colData[selKey(item)] || [];
                          const r = rows.find(x=>x.metric===row.metric);
                          TOP_CH.forEach(ch => {
                            if (!visibleCh.has(ch)) return;
                            const val = r?.channels[ch]?.selected_value ?? null;
                            if (val !== null) { sum += val; hasVal = true; }
                          });
                        });
                        totalVal = hasVal ? sum : null;
                      }

                      return (
                        <div style={{
                          width:COL_W,minWidth:COL_W,padding:"5px 4px",
                          borderLeft:"2px solid var(--accent-gold)",textAlign:"center",
                          backgroundColor:"rgba(201,168,76,0.04)",
                        }}>
                          <div className="num" style={{
                            fontSize:"11px",fontWeight:600,
                            color:valColor(totalVal,row.type),
                          }}>
                            {totalVal!==null
                              ? row.type==="percent" ? `${totalVal.toFixed(2)}%` : fmtVal(totalVal,row.type)
                              : "--"}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
